import React, { useState, useEffect, useRef, useContext } from 'react';
import { securePut, api, securePost } from '../config/api';
import { getResponseData } from '../config/api';
import { getJwkKeystore } from '../utils/encryption';
import { useTranslation } from 'react-i18next';
import { SaveCancel } from './PrimaryButton';
import { updateUser } from '../Shared/UserContext/Actions';
import UserContext from '../Shared/UserContext';
import SnackbarNotification from './SnackbarNotification';
import Input from './Input';
import DeleteIcon from '../icons/trashR.svg';

const DynamicForm = ({
  title,
  initialValues = {},
  baseFields = [],
  fileFields = null,
  onSubmit,
  onSuccess,
  onCancel,
  apiUrl,
  module,
}) => {
  const [formValues, setFormValues] = useState({});
  const [files, setFiles] = useState({});
  const [fileNames, setFileNames] = useState({});
  const [previews, setPreviews] = useState({});
  const [folderFiles, setFolderFiles] = useState({});
  const [folderNames, setFolderNames] = useState({});
  const inputRef = useRef(null);
  const [, setConditionalBaseFields] = useState([]);
  const [uploading, setUploading] = useState(false);
  const { t } = useTranslation();
  const { i18n } = useTranslation();
  const { dispatch } = useContext(UserContext);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const isInitialized = useRef(false);
  const hasTriggeredOnChange = useRef(false);

  const showSnackbar = (message, severity = 'success', manual = false) => {
    setSnackbar({ open: true, message, severity, manual });
  };

  useEffect(() => {
    // Only initialize form values once, when the component mounts
    // Don't reinitialize when baseFields changes (e.g., when dropdown options update)
    if (isInitialized.current) {
      return;
    }
    
    const initialFormValues = {};
    const initialConditionalFields = [];

    baseFields.forEach((field) => {
      if (field.type === "checkbox-dropdown" || field.name === "dropdown") {
        initialFormValues[field.name] = initialValues[field.name] || [];
      } else {
        // Use field.value first (set in ModuleWrapper), then initialValues
        initialFormValues[field.name] = field.value || initialValues[field.name] || "";
      }

      if (field.name === "role" && field.conditionalFields) {
        const roleValue = initialValues[field.name];
        if (roleValue && field.conditionalFields[roleValue]) {
          const relatedFields = field.conditionalFields[roleValue] || [];
          initialConditionalFields.push(...relatedFields);

          relatedFields.forEach((relatedField) => {
            if (relatedField.name === "repositories") {
              initialFormValues[relatedField.name] = initialValues.repositories?.map(
                (repo) => repo.repoId
              ) || [];
            } else {
              initialFormValues[relatedField.name] = initialValues[relatedField.name] || "";
            }
          });
        }
      }
    });

    setFormValues(initialFormValues);
    setConditionalBaseFields(initialConditionalFields);
    isInitialized.current = true;
    console.log("DynamicForm initialized with values:", initialFormValues);
    console.log("DynamicForm initialValues prop:", initialValues);
  }, [baseFields, initialValues]);

  // Trigger onChange callbacks for fields with initial values (e.g., for dependent dropdowns in modify mode)
  useEffect(() => {
    // Only trigger once after initialization
    if (!isInitialized.current || hasTriggeredOnChange.current) {
      return;
    }
    
    baseFields.forEach((field) => {
      if (field.onChange && initialValues[field.name]) {
        console.log(`Triggering onChange for ${field.name} with initial value:`, initialValues[field.name]);
        field.onChange(initialValues[field.name]);
      }
    });
    
    hasTriggeredOnChange.current = true;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInitialized.current]);

  useEffect(() => {
    if (initialValues.header) {
      const match = initialValues.header.match(/grayfox\/([^?]+)/);
      if (match) {
        setFileNames((prev) => ({
          ...prev,
          header: match[1],
        }));
      }
    }
    if (initialValues.image) {
      const match = initialValues.image.match(/grayfox\/([^?]+)/);
      if (match) {
        setFileNames((prev) => ({
          ...prev,
          image: match[1],
        }));
      }
    }

  }, [initialValues.image, initialValues.header]);


const handleInputChange = (name, value) => {
  // Reset file fields when documentType changes in documents form
  if (module === 'documents' && name === 'type') {
    // Clear all file-related state
    setFiles({});
    setFolderFiles({});
    setFolderNames({});
    setFileNames({});
    setPreviews(prev => {
      // Revoke all object URLs to prevent memory leaks
      Object.values(prev).forEach(url => {
        if (url) URL.revokeObjectURL(url);
      });
      return {};
    });
    // Clear file input values for all file fields
    if (fileFields?.length) {
      fileFields.forEach(field => {
        const fileInput = document.getElementById(`fileInput-${field.name}-folder`) || 
                         document.getElementById(`fileInput-${field.name}-file`) ||
                         document.getElementById(`fileInput-${field.name}`);
        if (fileInput) {
          fileInput.value = "";
        }
      });
    }
  }

  setFormValues(prev => {
    const nextValues = {
      ...prev,
      [name]: value,
    };

    if (fileFields?.length) {
      // Only add image if it's in fileFields
      const hasImageField = fileFields.some(field => field.name === 'image');
      if (hasImageField && !nextValues.image) {
        nextValues.image = initialValues.image || "";
      }
      // Only add header if it's in fileFields
      const hasHeaderField = fileFields.some(field => field.name === 'header');
      if (hasHeaderField && !nextValues.header) {
        nextValues.header = initialValues.header || "";
      }
    }
    return nextValues;
  });

  const fieldConfig = baseFields.find(field => field.name === name);
  
  if (fieldConfig?.clearFieldsOnChange && Array.isArray(fieldConfig.clearFieldsOnChange)) {
    setFormValues(prev => {
      const clearedValues = { ...prev };
      fieldConfig.clearFieldsOnChange.forEach(fieldName => {
        clearedValues[fieldName] = "";
        console.log(`Cleared field ${fieldName} due to change in ${name}`);
      });
      return clearedValues;
    });
  }
  
  if (fieldConfig?.conditionalFields) {
    const newFields = fieldConfig.conditionalFields[value] || [];
    setConditionalBaseFields(newFields);

    const newConditionalValues = {};
    newFields.forEach(field => {
      // Preserve existing value from initialValues if available, otherwise use default
      if (field.name === "repositories" && initialValues.repositories) {
        newConditionalValues[field.name] = initialValues.repositories.map(
          (repo) => repo.repoId
        ) || [];
      } else {
        newConditionalValues[field.name] =
          field.type === "checkbox-dropdown" 
            ? (initialValues[field.name] || [])
            : (initialValues[field.name] || "");
      }
    });

    setFormValues(prev => {
      const updatedValues = {
        ...prev,
        ...newConditionalValues,
      };
      
      if (fileFields?.length) {
        // Only add image if it's in fileFields
        const hasImageField = fileFields.some(field => field.name === 'image');
        if (hasImageField) {
          updatedValues.image = prev.image || initialValues.image || "";
        }
        // Only add header if it's in fileFields
        const hasHeaderField = fileFields.some(field => field.name === 'header');
        if (hasHeaderField) {
          updatedValues.header = prev.header || initialValues.header || "";
        }
      }
      
      return updatedValues;
    });
  }
  
  if (fieldConfig?.onChange) {
    fieldConfig.onChange(value);
  }
};

  const handleFileChange = (fieldName, file) => {
    if (file) {
      const previewUrl = URL.createObjectURL(file);
      setPreviews(prev => ({ ...prev, [fieldName]: previewUrl }));
      setFiles(prev => ({ ...prev, [fieldName]: file }));
      setFileNames(prev => ({ ...prev, [fieldName]: file.name }));
    }
  };

  const handleFolderChange = (fieldName, fileList) => {
    if (fileList && fileList.length > 0) {
      const filesArray = Array.from(fileList);
      setFolderFiles(prev => ({ ...prev, [fieldName]: filesArray }));
      setFileNames(prev => ({ ...prev, [fieldName]: `${filesArray.length} files selected` }));
      // Do not store or send folder name; file names are independent of their folder.
    }
  };

  const removeFile = (fieldName) => {
    URL.revokeObjectURL(previews[fieldName]);
    setPreviews(prev => {
      const updated = { ...prev };
      delete updated[fieldName];
      return updated;
    });
    setFiles(prev => {
      const updated = { ...prev };
      delete updated[fieldName];
      return updated;
    });
    setFileNames(prev => {
      const updated = { ...prev };
      delete updated[fieldName];
      return updated;
    });
  };

const handleSubmit = async (e) => {
  e.preventDefault();
  const requiredFields = [...baseFields, ...fileFields];
  const missing = requiredFields.filter(f => f.required && !formValues[f.name]);
  if (missing.length > 0) {
      showSnackbar(`Veuillez remplir les champs obligatoires : ${missing.map(f => f.label).join(', ')}`, 'error');
      return;
  }

  if (formValues.password && formValues.passwordConfirmation) {
    if (formValues.password !== formValues.passwordConfirmation) {
      showSnackbar(t('placeholder.passwordMismatch'), 'error');
      return;
    }
  }

  const formPayload = { ...formValues };

  // Coerce number-type base fields to actual numbers so they are sent as digits, not strings
  baseFields.forEach((field) => {
    if (field.type === 'number' && field.name in formPayload && formPayload[field.name] !== '' && formPayload[field.name] != null) {
      const n = Number(formPayload[field.name]);
      if (!Number.isNaN(n)) formPayload[field.name] = n;
    }
  });

  const uploadedFileNames = {};

  try {
    setUploading(true);

    if (fileFields?.length && Object.keys(files).length > 0) {
      const key = await getJwkKeystore();

      for (const [fieldName, file] of Object.entries(files)) {
        const formData = new FormData();
        formData.append('file', file);
        console.log("formData:", formData);
        try {
        // Use /attachments/files/add for documents and invoice_payment, otherwise use /attachments/images/add
        const uploadUrl = (module === 'documents') 
          ? '/attachments/files/add' 
          : '/attachments/images/add';
        const fileUploadResponse = await api.post(
          uploadUrl,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
              Authorization: localStorage.getItem('token'),
            },
          }
        );

        const decryptedResponse = await getResponseData(fileUploadResponse, key);
        if (decryptedResponse?.fileName) {
          uploadedFileNames[fieldName] = decryptedResponse.fileName;
        }
        } catch (error) {
          console.error("Error uploading file:", error.response?.data || error);
          showSnackbar(t('errors.generic'), 'error');
          return;
        }
      }
    }

    if (fileFields?.length) {
      // Get list of file field names
      const fileFieldNames = fileFields.map(field => field.name);
      
      fileFields.forEach((field) => {
        const fieldName = field.name;
        const uploaded = uploadedFileNames[fieldName];

        if (uploaded) {
          formPayload[fieldName] = uploaded;
        } else if (formValues[fieldName]) {
          const match = formValues[fieldName].match(/grayfox\/(.+?\.(?:jpg|jpeg|png))/i);
          if (match) {
            formPayload[fieldName] = match[1];
          }
        } else if (initialValues[fieldName]) {
          formPayload[fieldName] = initialValues[fieldName].split('/').pop().split('?')[0];
        } else {
          delete formPayload[fieldName];
        }
      });
      
      // Remove image and header from payload if they're not in fileFields
      if (!fileFieldNames.includes('image')) {
        delete formPayload.image;
      }
      if (!fileFieldNames.includes('header')) {
        delete formPayload.header;
      }
    } else {
      // If no fileFields, remove image and header
      delete formPayload.image;
      delete formPayload.header;
    }

    delete formPayload.passwordConfirmation;

    if (formValues.repositories && Array.isArray(formValues.repositories)) {
      const roleField = baseFields.find((field) => field.name === "role");
      const repositoriesField = roleField?.conditionalFields?.ROLE_TECHNICIAN?.find(
        (field) => field.name === "repositories"
      );

      if (repositoriesField?.options) {
        formPayload.repositories = formValues.repositories.map((repoId) => {
          const repo = repositoriesField.options.find((opt) => opt.value === repoId);
          return {
            repoActive: true,
            repoId,
            repoName: repo?.label || "",
          };
        });
      }
    }

    let response;

    if (title === t("form_titles.add_profile")) {
      const profileFieldsChanged = formValues.firstName || formValues.lastName || formValues.preferredLanguage;
      const passwordChanged = formValues.oldPassword && formValues.newPassword;

      if (profileFieldsChanged) {
        const profilePayload = {
          firstName: formValues.firstName,
          lastName: formValues.lastName,
          preferredLanguage: formValues.preferredLanguage,
        };

        response = await securePost('/users/updateProfile', profilePayload, showSnackbar);
        localStorage.setItem('user', JSON.stringify(response.data));
        dispatch(updateUser(response.data));

        if (profilePayload.preferredLanguage) {
          i18n.changeLanguage(profilePayload.preferredLanguage);
          localStorage.setItem('i18nextLng', profilePayload.preferredLanguage);
        }
      }

      if (passwordChanged) {
        response = await securePost('/users/password/change', {
          newPassword: formValues.newPassword,
          oldPassword: formValues.oldPassword,
        }, showSnackbar);
      }

    } else if (title === t("form_titles.add_settings")) {
      if ('header' in formPayload) {
        formPayload.headerImage = formPayload.header;
        delete formPayload.header;
      }

      if ('footer' in formPayload) {
        formPayload.footerText = formPayload.footer;
        delete formPayload.footer;
      }

      if ('language' in formPayload) {
        formPayload.preferredLanguage = formPayload.language;
        delete formPayload.language;
      }

      response = await securePost('/users/updateConfig', formPayload, showSnackbar);
      localStorage.setItem('settings', JSON.stringify(response.data));

    } else {
      console.log("payload:", formPayload);

    // For documents: if input is FOLDER use folder upload, else use single-file attachment add. Do not send input in payload.
    if (module === 'documents') {
      delete formPayload.input;
    }
    const isFolderMode = module === 'documents' && formValues.input === 'FOLDER';
    if (isFolderMode && Object.keys(folderFiles).length > 0) {
      try {
        const formData = new FormData();
        
        // Add all files from the folder with base name only (no folder path in fileName)
        const folderEntry = Object.entries(folderFiles)[0];
        if (folderEntry && folderEntry[1]) {
          const filesArray = folderEntry[1];
          filesArray.forEach((file) => {
            const baseName = file.webkitRelativePath
              ? file.webkitRelativePath.split('/').pop()
              : (file.name || 'file');
            const fileWithBaseName = new File([file], baseName, { type: file.type });
            formData.append('files', fileWithBaseName);
          });
        }
        
        // Add required fields (file and type only; no input). Do not send folder name so file names stay independent of folder.
        formData.append('receptionDocumentId', initialValues.receptionDocumentId);
        formData.append('type', formValues.type);

        // Use folder endpoint
        response = await api.post('/attachments/folders/add', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: localStorage.getItem('token'),
          },
        });

        onSubmit(response?.data);
        onSuccess();
        return;
      } catch (error) {
        console.error("Error uploading folder:", error.response?.data || error);
        showSnackbar(t('errors.generic'), 'error');
        return;
      } finally {
        setUploading(false);
      }
    }

    if (apiUrl.includes("/attachments/add") && Object.keys(files).length > 0) {
      try {
        const formData = new FormData();
        
        // Add the file (API expects field name "file"). Payload: file, type, receptionDocumentId; no input.
        const fileEntry = Object.entries(files)[0];
        if (fileEntry) {
          formData.append('file', fileEntry[1]);
        }
        if (formValues.type != null && formValues.type !== '') {
          formData.append('type', formValues.type);
        }        

        // Send the multipart request directly
        response = await api.post(apiUrl, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: localStorage.getItem('token'),
          },
        });

        onSubmit(response?.data);
        onSuccess();
        return;
      } catch (error) {
        console.error("Error uploading document:", error.response?.data || error);
        showSnackbar(t('errors.generic'), 'error');
        return;
      } finally {
        setUploading(false);
      }
    }
    else {


      if ((!initialValues.id && (apiUrl === "vehicles/add" || apiUrl === "reception-documents/add" || apiUrl.includes("/attachments/add") || apiUrl === "/vehicle-brands" || apiUrl === "/attachment-document-types")) || apiUrl.includes ("/mark-as-paid")) {
        response = await securePost(apiUrl, formPayload, showSnackbar);
      } else {
        response = await securePut(apiUrl, formPayload, showSnackbar);
      }
      }
    }

    onSubmit(response?.data);
    onSuccess();

  } catch (error) {
    console.error("Form submission error:", error);
  } finally {
    setUploading(false);
  }
};

  const handleCancel = () => {
    setFiles(null);
    setFileNames("");
    if (inputRef.current) {
      inputRef.current.value = "";
    }
    onCancel();
  };


  const passwordFields = [
    { label: t(`fields.users.oldPassword`), name: "oldPassword", type: "password", isPasswordConfirmation: true },
    { label: t(`fields.users.newPassword`), name: "newPassword", type: "password" },
    { label: t(`fields.users.passwordConfirmation`), name: "password_confirmation", type: "password", isPasswordConfirmation: true },
  ]

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-[1050]">
      <div className="bg-[#F9FAFC]  px-12 py-8 rounded-[12px] shadow-lg w-full max-w-4xl">
        <h2 className="text-[32px] text-[#38485C] font-semibold mb-6">{title}</h2>
        <form className="space-y-4">
          {/* Base Fields */}
          <div className="space-y-4">
            <div className={`grid gap-6 ${baseFields.filter(field => field.name !== "footer" && field.type !== "hidden").length === 4 ? 'md:grid-cols-2' : 'md:grid-cols-3'}`}>
              {baseFields
                .filter(field => field.name !== "footer" && field.type !== "hidden")
                .map((field, index) => {
                  const fieldValue = formValues[field.name] || "";
                  return (
                    <React.Fragment key={field.name || index}>
                      {/* Render Base Field */}
                      <Input
                        type={field.type || "text"}
                        label={field.label}
                        name={field.name}
                        options={field.options || []}
                        value={fieldValue}
                        required={field.required}
                        onChange={(value) => handleInputChange(field.name, value)}
                        isPasswordConfirmation={field.isPasswordConfirmation}
                        max={field.max}
                        min={field.min}
                      />

                    {field.conditionalFields &&
                      formValues[field.name] in field.conditionalFields &&
                      field.conditionalFields[formValues[field.name]].map((conditionalField, conditionalIndex) => (
                        <Input
                          key={`conditional-${index}-${conditionalIndex}`}
                          type={conditionalField.type || "text"}
                          label={conditionalField.label}
                          name={conditionalField.name}
                          options={conditionalField.options || []}
                          value={formValues[conditionalField.name] || ""}
                          required={conditionalField.required}
                          onChange={(value) => handleInputChange(conditionalField.name, value)}
                        />
                      ))}

                  </React.Fragment>
                );
              })}
            </div>
          </div>

          {/* Password Section */}
          {title === t("form_titles.add_profile") && passwordFields.length > 0 && (
            <div className="space-y-4 mt-6">
              <h3 className="text-[16px] font-semibold text-[#ACAFC6]">
                {t("fields_types.changePass")}
              </h3>
              <div className="grid md:grid-cols-3 gap-6">
                {passwordFields.map((field, index) => (
                  <Input
                    key={index}
                    type={field.type || "text"}
                    label={field.label}
                    name={field.name}
                    isPasswordConfirmation={field.isPasswordConfirmation}
                    value={formValues[field.name] || ""}
                    required={field.required}
                    onChange={(value) => handleInputChange(field.name, value)}
                  />
                ))}
              </div>
            </div>
          )}

          {/* File Fields Section */}
          {fileFields && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 gap-4">
                {fileFields.map((field, index) => {
                  const hasFile = Boolean(fileNames[field.name]);
                  const existingImage = initialValues[field.name];
                  const previewImage = previews[field.name];

                  const isFolderMode = module === 'documents' && formValues.input === 'FOLDER';

                  return (
                    <div key={index} className="space-y-4">
                      <h3 className="text-[16px] font-semibold text-[#ACAFC6]">{field.label}</h3>
                      <label
                        htmlFor={`fileInput-${field.name}-${isFolderMode ? 'folder' : 'file'}`}
                        className={` border-2  border-dashed h-[93px] rounded-md p-4 block cursor-pointer transition-colors
                ${hasFile ? 'border-gray-300' : 'border-gray-300'} `}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={(e) => {
                          e.preventDefault();
                          const droppedFile = e.dataTransfer.files?.[0];
                          if (droppedFile) {
                            // If accept is "*", allow all file types
                            if (field.accept === "*") {
                              handleFileChange(field.name, droppedFile);
                            } else {
                              const validTypes = field.accept.split(',').map(t => t.trim());
                              const isValid = validTypes.some(type => {
                                if (type === 'image/*') return droppedFile.type.startsWith('image/');
                                if (type.endsWith('/*')) {
                                  const baseType = type.split('/')[0];
                                  return droppedFile.type.startsWith(`${baseType}/`);
                                }
                                return droppedFile.type === type;
                              });

                              if (isValid) {
                                handleFileChange(field.name, droppedFile);
                              } else {
                                showSnackbar(`Invalid file type. Allowed: ${field.accept}`, 'error');
                              }
                            }
                          }
                        }}
                      >
                        <input
                          key={`fileInput-${field.name}-${isFolderMode ? 'folder' : 'file'}`}
                          id={`fileInput-${field.name}-${isFolderMode ? 'folder' : 'file'}`}
                          type="file"
                          name={field.name}
                          {...(isFolderMode 
                            ? { 
                                webkitdirectory: '', 
                                directory: '', 
                                multiple: true 
                              } 
                            : { 
                                accept: field.accept 
                              })}
                          className="hidden"
                          onChange={(e) => {
                            // Check if this is a folder upload (VEHICLE_PICTURE)
                            if (isFolderMode) {
                              const selectedFiles = e.target.files;
                              if (selectedFiles && selectedFiles.length > 0) {
                                handleFolderChange(field.name, selectedFiles);
                                e.target.value = null;
                              }
                              return;
                            }

                            const selectedFile = e.target.files?.[0];
                            if (selectedFile) {
                              // If accept is "*", allow all file types
                              if (field.accept === "*") {
                                handleFileChange(field.name, selectedFile);
                                e.target.value = null;
                              } else {
                                const validTypes = field.accept.split(',').map(t => t.trim());
                                const isValid = validTypes.some(type => {
                                  if (type === 'image/*') return selectedFile.type.startsWith('image/');
                                  if (type.endsWith('/*')) {
                                    const baseType = type.split('/')[0];
                                    return selectedFile.type.startsWith(`${baseType}/`);
                                  }
                                  return selectedFile.type === type;
                                });

                                if (isValid) {
                                  handleFileChange(field.name, selectedFile);
                                  e.target.value = null;
                                } else {
                                  showSnackbar(`Invalid file type. Allowed: ${field.accept}`, 'error');
                                  e.target.value = null;
                                }
                              }
                            }
                          }}
                        />

                        <div className="flex items-center justify-between gap-3 h-full min-h-[40px]">
                          {(hasFile || (isFolderMode && folderFiles[field.name])) ? (
                            <>
                              <div className="flex items-center ml-[40px] min-w-0 flex-1 h-full">
                                {(previewImage || existingImage) && !isFolderMode && (
                                  <div className="relative w-20 h-20 shrink-1">
                                    <img
                                      src={previewImage || existingImage}
                                      alt="Preview"
                                      className="w-full h-full object-contain rounded-md"
                                    />
                                  </div>
                                )}
                                {isFolderMode && folderFiles[field.name] && (
                                  <div className="text-[14px] font-semibold text-gray-600">
                                    {folderFiles[field.name].length} fichier(s) sélectionné(s)
                                  </div>
                                )}
                                {!isFolderMode && (
                                  <span className="text-[14px] font-semibold ml-[20px] text-[#38475D] truncate">
                                    {fileNames[field.name]}
                                  </span>
                                )}
                              </div>

                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  removeFile(field.name);
                                  if (isFolderMode) {
                                    setFolderFiles(prev => {
                                      const updated = { ...prev };
                                      delete updated[field.name];
                                      return updated;
                                    });
                                    setFolderNames(prev => {
                                      const updated = { ...prev };
                                      delete updated[field.name];
                                      return updated;
                                    });
                                  }
                                }}
                                className="w-8 h-8 p-1 -m-1 mr-[36px] flex items-center justify-center bg-[#F9FAFC] rounded-[12px] border border-[#EF466F4D]"
                              >
                                <img src={DeleteIcon} alt="Delete" className="w-5 h-5" />
                              </button>
                            </>
                          ) : (
                            <div className="flex items-center justify-center w-full gap-4 text-center">
                              <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                              </svg>
                              <div className="text-left">
                                <p className="text-sm text-gray-500">
                                  {isFolderMode 
                                    ? 'Choisissez un dossier pour télécharger' 
                                    : field.placeholderText1}
                                </p>
                                <p className="text-sm font-bold text-blue-500">
                                  {isFolderMode 
                                    ? 'Folder upload' 
                                    : field.placeholderText2}
                                </p>
                              </div>
                            </div>
                          )}
                        </div>

                      </label>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Footer Text Area (Displayed Separately) */}
          {baseFields.some(field => field.name === "footer") && (
            <div className="space-y-4">
              <div className="col-span-1">
                <label className="text-[16px] font-semibold text-[#ACAFC6] block mb-4">
                  {t(`fields.settings.footer`).toUpperCase()}
                </label>
                <textarea
                  className="w-full h-32 p-3 border border-[#E3E3E3] rounded-lg 
              focus:border-[#6A79F8] focus:outline-none hover:border-[#6A79F8] text-[16px] font-medium bg-white text-[#000000]"
                  name="footer"
                  value={formValues.footer || ""}
                  onChange={(e) => handleInputChange("footer", e.target.value)}
                  placeholder={"..."}
                />
              </div>
            </div>
          )}


          {/* Footer Buttons */}
          <div className="flex justify-end space-x-4 mt-6">
            <SaveCancel
              onCancelClick={handleCancel}
              onSaveClick={handleSubmit}
              loading={uploading}
            />
          </div>
        </form>
      </div>
      <SnackbarNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        manual={snackbar.manual}
      />
    </div>
  );

};

export default DynamicForm;
